import React, { useState, useRef, useEffect } from 'react';
import { X, Lock, Info } from 'lucide-react';
import './otpVerification.css';
import { CrossButton } from 'components';

const OtpVerificationModal = ({ 
    isOpen, 
    onClose, 
    onVerify, 
    onResendOtp,
    phoneNumber = "9977800300",
    otpValidityMinutes = 10 
}) => {
    const [otp, setOtp] = useState(['', '', '', '']);
    const [timer, setTimer] = useState(30);
    const [canResend, setCanResend] = useState(false);
    const inputRefs = useRef([]);

    // Reset OTP and timer when modal opens
    useEffect(() => {
        if (isOpen) {
            setOtp(['', '', '', '']);
            setTimer(30);
            setCanResend(false);
            // Focus first input when modal opens
            setTimeout(() => {
                inputRefs.current[0]?.focus();
            }, 100);
        }
    }, [isOpen]);

    // Timer countdown
    useEffect(() => {
        let interval;
        if (isOpen && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => {
                    if (prev <= 1) {
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isOpen, timer]);

    // Handle input change
    const handleChange = (index, value) => {
        // Only allow single digit
        if (value.length > 1) {
            value = value.slice(-1);
        }

        // Only allow numbers
        if (!/^\d*$/.test(value)) {
            return;
        }

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Auto focus to next input
        if (value && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }

        // Check if all 4 digits are filled
        if (newOtp.every(digit => digit !== '')) {
            // Call API after a small delay
            setTimeout(() => {
                handleVerify(newOtp.join(''));
            }, 100);
        }
    };

    // Handle backspace
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    // Handle paste
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 4);
        
        if (!/^\d+$/.test(pastedData)) {
            return;
        }

        const newOtp = [...otp];
        pastedData.split('').forEach((char, index) => {
            if (index < 4) {
                newOtp[index] = char;
            }
        });
        
        setOtp(newOtp);

        // Focus last filled input or next empty
        const lastFilledIndex = Math.min(pastedData.length - 1, 3);
        inputRefs.current[lastFilledIndex]?.focus();

        // Auto verify if all filled
        if (newOtp.every(digit => digit !== '')) {
            setTimeout(() => {
                handleVerify(newOtp.join(''));
            }, 100);
        }
    };

    // API call function
    const handleVerify = async (otpValue) => {
        if (onVerify) {
            try {
                await onVerify(otpValue);
                // Reset OTP after successful verification
                setOtp(['', '', '', '']);
            } catch (error) {
                // Reset OTP on error
                setOtp(['', '', '', '']);
                inputRefs.current[0]?.focus();
            }
        }
    };

    // Handle resend OTP
    const handleResendOtp = async () => {
        if (canResend && onResendOtp) {
            try {
                await onResendOtp();
                setTimer(30);
                setCanResend(false);
                setOtp(['', '', '', '']);
                inputRefs.current[0]?.focus();
            } catch (error) {
                console.error('Failed to resend OTP:', error);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div className="otp-modal-backdrop" ></div>

            {/* Modal */}
            <div className="otp-modal">
                <div className="otp-modal-header">
                    <h3 className="otp-modal-title">Verify OTP</h3>
                    <CrossButton type="button" className="_switchBtn  __animEffect_btn" style={{ marginLeft: 'auto' }} onClick={onClose}>
                        <svg className="cross" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none" >
                            <path
                                d="M12.5321 0.477671C12.4422 0.387555 12.3353 0.316061 12.2177 0.267281C12.1001 0.2185 11.9741 0.193391 11.8468 0.193391C11.7195 0.193391 11.5934 0.2185 11.4758 0.267281C11.3582 0.316061 11.2514 0.387555 11.1615 0.477671L6.40798 5.22143L1.6545 0.46795C1.56451 0.377952 1.45766 0.306563 1.34008 0.257857C1.22249 0.20915 1.09646 0.184082 0.969185 0.184082C0.84191 0.184082 0.715881 0.20915 0.598294 0.257857C0.480707 0.306563 0.373865 0.377952 0.283868 0.46795C0.19387 0.557947 0.122481 0.664789 0.0737746 0.782376C0.0250685 0.899963 -9.48274e-10 1.02599 0 1.15327C9.48275e-10 1.28054 0.0250684 1.40657 0.0737746 1.52416C0.122481 1.64175 0.19387 1.74859 0.283868 1.83858L5.03735 6.59206L0.283868 11.3455C0.19387 11.4355 0.122481 11.5424 0.0737746 11.66C0.0250685 11.7776 0 11.9036 0 12.0309C0 12.1581 0.0250685 12.2842 0.0737746 12.4017C0.122481 12.5193 0.19387 12.6262 0.283868 12.7162C0.373865 12.8062 0.480707 12.8776 0.598294 12.9263C0.715881 12.975 0.84191 13 0.969185 13C1.09646 13 1.22249 12.975 1.34008 12.9263C1.45766 12.8776 1.56451 12.8062 1.6545 12.7162L6.40798 7.9627L11.1615 12.7162C11.2515 12.8062 11.3583 12.8776 11.4759 12.9263C11.5935 12.975 11.7195 13 11.8468 13C11.974 13 12.1001 12.975 12.2177 12.9263C12.3353 12.8776 12.4421 12.8062 12.5321 12.7162C12.6221 12.6262 12.6935 12.5193 12.7422 12.4017C12.7909 12.2842 12.816 12.1581 12.816 12.0309C12.816 11.9036 12.7909 11.7776 12.7422 11.66C12.6935 11.5424 12.6221 11.4355 12.5321 11.3455L7.77861 6.59206L12.5321 1.83858C12.9015 1.46919 12.9015 0.847062 12.5321 0.477671Z"
                                fill="black"
                            />
                        </svg>
                    </CrossButton>
                </div>

                <div className="otp-modal-body">
                    {/* Lock Icon */}
                    <div className="otp-lock-icon">
                        <Lock size={32} color="#fff" />
                    </div>

                    {/* Title */}
                    <h2 className="otp-verify-title">Verify OTP</h2>
                    <p className="otp-verify-subtitle">
                        Enter the 4-digit code sent to Whatsapp<br />
                        {phoneNumber}
                    </p>

                    {/* OTP Input Boxes */}
                    <div className="otp-input-container">
                        {otp.map((digit, index) => (
                            <input
                                key={index}
                                ref={(el) => (inputRefs.current[index] = el)}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                onPaste={handlePaste}
                                className="otp-input-box"
                            />
                        ))}
                    </div>

                    {/* Resend OTP Timer */}
                    <div className="otp-resend-container">
                        {canResend ? (
                            <button className="otp-resend-link" onClick={handleResendOtp}>
                                Resend OTP
                            </button>
                        ) : (
                            <p className="otp-timer-text">
                                Resend OTP in <span className="otp-timer-highlight">{timer} seconds</span>
                            </p>
                        )}
                    </div>

                    {/* Info Message */}
                    <div className="otp-info-box">
                        <div className="otp-info-icon">
                            <Info size={16} color="#3b82f6" />
                        </div>
                        <p className="otp-info-text">
                            This additional security layer ensures your payment is authorized by you. 
                            The OTP is valid for {otpValidityMinutes} minutes.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default OtpVerificationModal;